import React, { useState } from "react";
import { Box, Button, Chip, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { CanvasSignature } from "../../../../shared/UIParts/CanvasSignature";
import DraftFormDialog from "../TaskForm/DraftFormDialog";
import { PreparedFormItem, PreparedForms } from "./usePreparedForms";

interface Props {
  items: PreparedFormItem[];
  forms: PreparedForms;
  disabled?: boolean;
}

/**
 * "הכנת הטפסים" - the step of a dialog that creates tasks: for each task whose producer has a form in the system,
 * preparing it now (edited here, saved on the task and sent to the client for signature) or later in an external
 * forms system; and the licence holder's signature when none is saved in their profile.
 */
const PreparedFormsStep: React.FC<Props> = ({ items, forms, disabled }) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing = editingKey ? forms.forms[editingKey] : undefined;
  const editingItem = items.find((item) => item.key === editingKey);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>הכנת הטפסים</Typography>
        <Typography variant="body2" color="text.secondary">
          להכין את הטפסים עכשיו? טופס שמוכן כאן נשמר במשימה ונשלח ללקוח לחתימה בלינק. אם תכינו אחר כך במערכת אחרת,
          תשלחו אותו ללקוח משם (למשל בוואטסאפ).
        </Typography>
      </Box>

      <Box>
        {items.map((item) => {
          const form = forms.forms[item.key];
          return (
            <Box key={item.key} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 200 }}>
                  {item.label}
                </Typography>
                {!form || form.loading ? (
                  <CircularProgress size={18} />
                ) : !form.draft ? (
                  <Chip size="small" variant="outlined" label="אין טופס במערכת - הכנה במערכת חיצונית" />
                ) : (
                  <ToggleButtonGroup
                    size="small" exclusive value={form.now ? "now" : "later"} disabled={disabled}
                    onChange={(_e, v) => v && forms.setNow(item.key, v === "now")}
                  >
                    <ToggleButton value="now">עכשיו במערכת</ToggleButton>
                    <ToggleButton value="later">אחר כך (מערכת חיצונית)</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
              {form?.draft && form.now && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setEditingKey(item.key)} disabled={disabled}>
                    {`עריכת הטופס (${form.draft.template_name ?? ""})`}
                  </Button>
                  {form.problems.length > 0 ? (
                    <Typography variant="caption" color="warning.dark">{`לתקן: ${form.problems.join(" · ")}`}</Typography>
                  ) : (
                    <Chip size="small" color="success" variant="outlined" label="מוכן לחתימה" />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {forms.needsDrawnSignature && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            חתימת בעל הרישיון (לא נמצאה חתימה שמורה בפרופיל) - בחתימה את/ה מאשר/ת את הצהרת בעל הרישיון שבטפסים:
          </Typography>
          <CanvasSignature onChange={forms.setAgentSignature} height={120} />
        </Box>
      )}

      {editing?.draft && editingItem && (
        <DraftFormDialog
          open
          title={editingItem.label}
          draft={editing.draft}
          values={editing.values}
          onClose={(values) => {
            setEditingKey(null);
            forms.setValues(editingItem.key, values);
          }}
        />
      )}
    </Box>
  );
};

export default PreparedFormsStep;
